import React from 'react';
import { Input, Button, Checkbox, Radio, Select, message } from 'antd';
import {
  UserOutlined, MailOutlined, WalletOutlined, BankOutlined, NumberOutlined, IdcardOutlined,
} from '@ant-design/icons';
import { usePaymentProfile, PaymentProfileFields } from '../context/payment-profile';
import { COUNTRIES } from '../config/countries';

const grey = { color: '#9ca3af' };

// Formato mínimo de email (*@* — algo antes y después de la arroba, sin espacios).
const EMAIL_RE = /^[^@\s]+@[^@\s]+$/;

// Título de campo (compartido entre los Input y el Select/Radio de la sección de cobro).
const labelStyle = (error?: boolean): React.CSSProperties => ({
  fontSize: 13, fontWeight: 600, marginBottom: 4, color: error ? '#cf1322' : undefined,
});

/**
 * Field DEBE vivir FUERA de PaymentDataForm: definido adentro se recrea en cada render,
 * React desmonta/remonta el Input y el usuario pierde el foco en cada tecla (bug 2026-07-01).
 */
const Field: React.FC<{
  text: string; icon: React.ReactNode; value: string; placeholder: string;
  onChange: (v: string) => void; error?: boolean; errorMsg?: string;
}> = ({ text, icon, value, placeholder, onChange, error, errorMsg }) => (
  <div>
    <div style={labelStyle(error)}>{text}</div>
    <Input
      prefix={icon}
      placeholder={placeholder}
      status={error ? 'error' : undefined}
      value={value}
      onChange={ev => onChange(ev.target.value)}
    />
    {error && errorMsg && <div style={{ color: '#cf1322', fontSize: 12, marginTop: 4 }}>{errorMsg}</div>}
  </div>
);

/**
 * Form reusable de datos personales. Lo usan el popup ("Completa tus datos personales")
 * y la sección de Settings ("Datos Personales"). Update parcial: guarda lo cargado.
 *
 * Reglas (feedback 2026-07-30):
 *  - OBLIGATORIOS para guardar: Nombre + Email con formato *@* + Términos aceptados +
 *    confirmación de mayoría de edad. Sin los 4 no se guarda (y el popup no se puede
 *    cerrar — ver payment-data-gate).
 *  - Los dos checks (términos y mayoría de edad) arrancan DESTILDADOS. Una vez
 *    aceptados y persistidos, quedan tildados y bloqueados (no se des-aceptan).
 *  - `withPaymentData` (solo Settings, NO el popup) agrega los datos de COBRO: País +
 *    método de cobro (Mercado Pago → alias / transferencia bancaria → banco + nº de
 *    cuenta + documento del titular). No bloquean el guardado — su ausencia dispara el
 *    cartel ⚠️ "faltan datos para el pago" (use-profile-completeness), no un error acá.
 *  - Los errores en rojo aparecen recién tras el primer intento de guardar (no de entrada).
 */
export const PaymentDataForm: React.FC<{
  submitLabel?: string;
  emailLabel?: string;
  /** true = muestra y guarda también País + método de cobro (sección de Settings). */
  withPaymentData?: boolean;
  onSaved?: () => void;
}> = ({ submitLabel = 'Guardar cambios', emailLabel = 'Correo electrónico', withPaymentData = false, onSaved }) => {
  const { profile, save } = usePaymentProfile();
  const [fullName, setFullName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [terms, setTerms] = React.useState(false);   // destildado por DEFECTO (feedback 2026-07-13)
  const [adult, setAdult] = React.useState(false);   // ídem términos (feedback 2026-07-30)
  const [country, setCountry] = React.useState('');
  const [method, setMethod] = React.useState('');    // '' | 'mercadopago' | 'bank_transfer'
  const [mpAlias, setMpAlias] = React.useState('');
  const [bankName, setBankName] = React.useState('');
  const [bankAccount, setBankAccount] = React.useState('');
  const [bankDocument, setBankDocument] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [attempted, setAttempted] = React.useState(false);

  React.useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || '');
      setEmail(profile.email || '');
      setCountry(profile.country || '');
      setMethod(profile.payment_method || '');
      setMpAlias(profile.mercadopago_alias || '');
      setBankName(profile.bank_name || '');
      setBankAccount(profile.bank_account_number || '');
      setBankDocument(profile.bank_holder_document || '');
      // Nunca des-tildar por el perfil: si ya los aceptó (persistido), queda tildado y locked.
      if (profile.terms_accepted) setTerms(true);
      if (profile.adult_confirmed) setAdult(true);
    }
  }, [profile]);

  // Requisitos OBLIGATORIOS: nombre + email válido + términos + mayor de edad.
  const nameOk     = !!fullName.trim();
  const emailValid = EMAIL_RE.test(email.trim());
  const canSave    = nameOk && emailValid && terms && adult;

  const nameErr    = attempted && !nameOk;
  const emailErr   = attempted && !emailValid;
  const emailErrMsg = !email.trim() ? 'Completá tu correo electrónico.' : 'Ingresá un email válido (con @).';
  // Igual que los campos: si intentó guardar sin aceptar, se marca en rojo.
  // (bug 2026-07-16: los obligatorios sin feedback dejaban al usuario sin entender
  // por qué Aceptar no hacía nada.)
  const termsErr   = attempted && !terms;
  const adultErr   = attempted && !adult;

  // Los dos checks, una vez aceptados y persistidos en DB, quedan tildados y bloqueados.
  const termsLocked = !!profile?.terms_accepted;
  const adultLocked = !!profile?.adult_confirmed;

  const handleSave = async () => {
    setAttempted(true);
    if (!canSave) return;   // falta un obligatorio → mostramos rojo y NO guardamos ni cerramos.
    setSaving(true);
    try {
      const fields: PaymentProfileFields = {
        full_name: fullName, email, terms_accepted: terms, adult_confirmed: adult,
      };
      // Los datos de cobro solo viajan desde Settings: el popup ni los muestra ni
      // los pisa (update parcial en el backend — lo no enviado queda como está).
      if (withPaymentData) {
        fields.country = country;
        fields.payment_method = method;
        fields.mercadopago_alias = mpAlias;
        fields.bank_name = bankName;
        fields.bank_account_number = bankAccount;
        fields.bank_holder_document = bankDocument;
      }
      await save(fields);
      message.success('Datos guardados');
      onSaved?.();
    } catch (e) {
      message.error((e as Error).message || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Field text='Nombre' icon={<UserOutlined style={grey} />} value={fullName}
             placeholder='Ingresa tu nombre' onChange={setFullName}
             error={nameErr} errorMsg='Completá tu nombre.' />
      <Field text={emailLabel} icon={<MailOutlined style={grey} />} value={email}
             placeholder='tu@email.com' onChange={setEmail}
             error={emailErr} errorMsg={emailErrMsg} />

      {withPaymentData && (
        <>
          {/* País — necesario para saber cómo pagarte (pedido 2026-07-30). Su ausencia
              no bloquea el guardado: dispara el cartel ⚠️ de "faltan datos para el pago". */}
          <div>
            <div style={labelStyle()}>País</div>
            <Select
              showSearch
              value={country || undefined}
              placeholder='Seleccioná tu país'
              style={{ width: '100%' }}
              onChange={setCountry}
              options={COUNTRIES.map(c => ({ value: c, label: c }))}
            />
          </div>

          {/* Método de cobro: Mercado Pago (alias) o transferencia bancaria (banco +
              nº de cuenta + documento del titular — lo esencial para recibir una
              transferencia en cualquier país de la región, p.ej. Paraguay). */}
          <div>
            <div style={labelStyle()}>Método de cobro</div>
            <Radio.Group value={method || undefined} onChange={e => setMethod(e.target.value)}>
              <Radio.Button value='mercadopago'>Mercado Pago</Radio.Button>
              <Radio.Button value='bank_transfer'>Transferencia bancaria</Radio.Button>
            </Radio.Group>
          </div>
          {method === 'mercadopago' && (
            <Field text='Alias de Mercado Pago' icon={<WalletOutlined style={grey} />} value={mpAlias}
                   placeholder='tu.alias.mp' onChange={setMpAlias} />
          )}
          {method === 'bank_transfer' && (
            <>
              <Field text='Banco' icon={<BankOutlined style={grey} />} value={bankName}
                     placeholder='Nombre de tu banco' onChange={setBankName} />
              <Field text='Número de cuenta' icon={<NumberOutlined style={grey} />} value={bankAccount}
                     placeholder='CBU / CVU / IBAN o nº de cuenta según tu país' onChange={setBankAccount} />
              <Field text='Documento del titular' icon={<IdcardOutlined style={grey} />} value={bankDocument}
                     placeholder='DNI / CI / Cédula del titular de la cuenta' onChange={setBankDocument} />
            </>
          )}
        </>
      )}

      <div>
        <Checkbox checked={terms} disabled={termsLocked} onChange={e => setTerms(e.target.checked)}
                  style={termsErr ? { color: '#cf1322' } : undefined}>
          Acepto los{' '}
          <a href='/terms' target='_blank' rel='noreferrer'>Términos y Condiciones</a>
          {' '}y la{' '}
          <a href='/privacy' target='_blank' rel='noreferrer'>Política de Privacidad</a>
        </Checkbox>
        {termsErr && (
          <div style={{ color: '#cf1322', fontSize: 12, marginTop: 4 }}>
            Tenés que aceptar los Términos y Condiciones para continuar.
          </div>
        )}
      </div>

      {/* Mayoría de edad — mismo comportamiento que los términos (pedido 2026-07-30):
          obligatorio, destildado por defecto, y bloqueado una vez persistido. */}
      <div>
        <Checkbox checked={adult} disabled={adultLocked} onChange={e => setAdult(e.target.checked)}
                  style={adultErr ? { color: '#cf1322' } : undefined}>
          Confirmo que soy mayor de 18 años
        </Checkbox>
        {adultErr && (
          <div style={{ color: '#cf1322', fontSize: 12, marginTop: 4 }}>
            Tenés que confirmar que sos mayor de edad para continuar.
          </div>
        )}
      </div>

      {/* El botón va SIEMPRE alineado a la derecha; su posición no depende de ningún estado.
          (bug 2026-07-13: antes justifyContent dependía de onCancel → el botón saltaba de lado
          al tildar/destildar los términos.) */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
        {/* El botón queda SIEMPRE habilitado: al tocarlo con datos incompletos marca en rojo
            lo que falta (guía al usuario) en vez de quedar deshabilitado sin explicación. Solo
            guarda/cierra cuando los 4 obligatorios están OK (handleSave chequea canSave). */}
        <Button type='primary' loading={saving} onClick={handleSave}>
          {submitLabel}
        </Button>
      </div>
    </div>
  );
};
